import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd, debounce } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    /* TD CHANGE START: --total-header-height for drawer offset */
    this.#calculateHeaderHeight();
    window.addEventListener('resize', this.#handleResize);
    /* TD CHANGE END: --total-header-height for drawer offset */
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    /* TD CHANGE START: --total-header-height for drawer offset */
    this.#handleResize?.cancel?.();
    window.removeEventListener('resize', this.#handleResize);
    /* TD CHANGE END: --total-header-height for drawer offset */
  }

  /* TD CHANGE START: sticky-aware header bottom → --total-header-height */
  #handleResize = debounce(() => {
    this.#calculateHeaderHeight();
  }, 100);

  #calculateHeaderHeight = () => {
    const header = document.querySelector('header');
    if (!header) return;

    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));

    document.documentElement.style.setProperty('--total-header-height', `${bottom}px`);
  };
  /* TD CHANGE END: sticky-aware header bottom → --total-header-height */

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    /* TD CHANGE START: JS owns open state — block native <details> toggle */
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    /* TD CHANGE END: JS owns open state — block native <details> toggle */
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    /* TD CHANGE START: JS owns open state — block native <details> toggle */
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    /* TD CHANGE END: JS owns open state — block native <details> toggle */

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    /* TD CHANGE START: set open explicitly after suppressing native toggle */
    details.setAttribute('open', '');
    /* TD CHANGE END: set open explicitly after suppressing native toggle */

    /* TD CHANGE START: re-measure after announcement may have scrolled away */
    this.#calculateHeaderHeight();
    /* TD CHANGE END: re-measure after announcement may have scrolled away */

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      /* TD CHANGE START: trap focus next tick; onAnimationEnd may never fire */
      setTimeout(() => trapFocus(details), 0);
      /* TD CHANGE END: trap focus next tick; onAnimationEnd may never fire */
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    /* TD CHANGE START: reset on a timer; onAnimationEnd may never fire */
    setTimeout(() => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    }, 200);
    /* TD CHANGE END: reset on a timer; onAnimationEnd may never fire */
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
